import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Image as ImageIcon, MessageCircle, Pencil, Search, Send, Trash2, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";

function Avatar({ user, className = "" }) {
  const name = user?.name || "Membre";
  const initial = name.slice(0, 1).toUpperCase();
  if (user?.picture) {
    return (
      <img
        src={fileUrl(user.picture)}
        alt={name}
        className={`rounded-full object-cover bg-emerald-50 ${className}`}
        style={{ objectPosition: user.profile_picture_position || "center center" }}
      />
    );
  }
  return (
    <div className={`rounded-full bg-emerald-100 text-brand-green font-bold flex items-center justify-center ${className}`}>
      {initial}
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [error, setError] = useState("");

  const selectedId = selected?.user_id || searchParams.get("to") || "";

  const loadConversations = async () => {
    const { data } = await api.get("/dm/conversations");
    setConversations(data || []);
  };

  const loadUsers = async (q = "") => {
    const { data } = await api.get(`/dm/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setUsers(data || []);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await loadConversations();
      } catch (err) {
        setError(err?.response?.data?.detail || "Impossible de charger les messages.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setUsers([]);
      return undefined;
    }

    const timer = setTimeout(() => loadUsers(trimmedQuery).catch(() => {}), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const allContacts = useMemo(() => {
    const map = new Map();
    conversations.forEach((c) => map.set(c.other_user.user_id, { ...c.other_user, conversation: c }));

    if (query.trim()) {
      users.forEach((u) => {
        if (!map.has(u.user_id)) map.set(u.user_id, u);
      });
    }

    return Array.from(map.values());
  }, [conversations, users, query]);

  const openConversation = async (contact) => {
    setSelected(contact);
    setSearchParams({ to: contact.user_id });
    setEditingId(null);
    setEditingBody("");
    setError("");
    try {
      const { data } = await api.get(`/dm/conversations/${contact.user_id}`);
      setThread(data);
      await loadConversations();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’ouvrir cette conversation.");
    }
  };

  useEffect(() => {
    if (!selectedId || loading || selected) return;

    const contact = allContacts.find((c) => c.user_id === selectedId);
    if (contact) {
      openConversation(contact);
      return;
    }

    const openFromUrl = async () => {
      try {
        const { data } = await api.get(`/dm/conversations/${selectedId}`);
        if (data?.other_user) {
          setSelected(data.other_user);
          setThread(data);
        }
      } catch {
        setSearchParams({});
      }
    };

    openFromUrl();
  }, [selectedId, loading, allContacts, selected, setSearchParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!selected?.user_id || (!body.trim() && !imageUrl)) return;
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(`/dm/conversations/${selected.user_id}/messages`, { body, image_url: imageUrl || null });
      setThread((prev) => ({
        ...(prev || { other_user: selected, messages: [] }),
        messages: [...((prev && prev.messages) || []), data],
      }));
      setBody("");
      setImageUrl("");
      setImagePreview("");
      await loadConversations();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’envoyer le message.");
    } finally {
      setSending(false);
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg.message_id);
    setEditingBody(msg.body || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingBody("");
  };

  const saveEdit = async (messageId) => {
    if (!editingBody.trim()) return;
    setError("");
    try {
      const { data } = await api.patch(`/dm/messages/${messageId}`, { body: editingBody });
      setThread((prev) => ({
        ...(prev || { other_user: selected, messages: [] }),
        messages: ((prev && prev.messages) || []).map((m) => m.message_id === messageId ? data : m),
      }));
      cancelEdit();
      await loadConversations();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de modifier ce message.");
    }
  };

  const deleteMessage = async (msg) => {
    const confirmed = window.confirm("Supprimer ce message de ta conversation ?");
    if (!confirmed) return;
    setError("");
    try {
      await api.delete(`/dm/messages/${msg.message_id}`);
      setThread((prev) => ({
        ...(prev || { other_user: selected, messages: [] }),
        messages: ((prev && prev.messages) || []).filter((m) => m.message_id !== msg.message_id),
      }));
      await loadConversations();
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de supprimer ce message.");
    }
  };

  const deleteConversation = async () => {
    if (!selected?.user_id) return;
    const confirmed = window.confirm(`Supprimer toute la conversation avec ${selected.name || "ce membre"} ?\n\nElle sera supprimée de ton espace uniquement.`);
    if (!confirmed) return;
    setDeletingConversation(true);
    setError("");
    try {
      const deletedUserId = selected.user_id;
      await api.delete(`/dm/conversations/${deletedUserId}`);
      setThread(null);
      setSelected(null);
      setSearchParams({});
      setQuery("");
      setUsers([]);
      setConversations((prev) => prev.filter((c) => c.other_user?.user_id !== deletedUserId));
      await loadConversations();
      window.location.assign("/messages");
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de supprimer cette conversation.");
    } finally {
      setDeletingConversation(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = /\.(jpg|jpeg|png|webp)$/i;
    if (!allowedExtensions.test(file.name)) {
      setError("Fichier refusé. Formats acceptés : JPG, PNG, WEBP.");
      e.target.value = "";
      return;
    }

    setUploadingImage(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!data?.url) {
        throw new Error("Upload réussi, mais aucune URL retournée.");
      }

      setImageUrl(data.url);
      setImagePreview(data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Impossible d’ajouter cette image.");
      setImageUrl("");
      setImagePreview("");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const removePendingImage = () => {
    setImageUrl("");
    setImagePreview("");
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="messages-page">
      <div className="mb-8">
        <p className="label">Espace privé</p>
        <h1 className="font-heading text-4xl font-black">Messages directs</h1>
        <p className="text-gray-600 mt-2">Échange en privé avec les autres apprenants de SF Learning.</p>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <aside className="card overflow-hidden">
          <div className="p-4 border-b border-emerald-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="input pl-9"
                placeholder="Rechercher un membre..."
              />
            </div>
          </div>

          <div className="max-h-[68vh] overflow-y-auto divide-y divide-emerald-100">
            {allContacts.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">Aucun membre trouvé.</div>
            ) : allContacts.map((contact) => {
              const conv = contact.conversation;
              const active = selected?.user_id === contact.user_id;
              return (
                <button
                  key={contact.user_id}
                  type="button"
                  onClick={() => openConversation(contact)}
                  className={`w-full p-4 text-left flex items-center gap-3 transition ${active ? "bg-emerald-50" : "hover:bg-emerald-50/60"}`}
                >
                  <Avatar user={contact} className="h-11 w-11 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{contact.name}</p>
                      {conv?.unread_count > 0 && <span className="ml-auto rounded-full bg-brand-orange px-2 py-0.5 text-xs font-bold text-white">{conv.unread_count}</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{conv?.last_message?.body || (conv?.last_message?.image_url ? "📷 Photo" : (contact.role === "admin" ? "Admin" : "Membre"))}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="card min-h-[70vh] flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10 text-gray-500">
              <MessageCircle className="h-12 w-12 text-emerald-200 mb-3" />
              <p className="font-semibold text-gray-700">Sélectionne un membre</p>
              <p className="text-sm mt-1">Choisis un apprenant à gauche pour démarrer une conversation privée.</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-emerald-100 flex items-center justify-between gap-3 bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar user={selected} className="h-11 w-11 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-lg truncate">{selected.name}</p>
                    <p className="text-xs text-gray-500">Conversation privée</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={deleteConversation}
                  disabled={deletingConversation}
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-500 transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  title="Supprimer cette conversation"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Supprimer</span>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 bg-emerald-50/30">
                {(thread?.messages || []).length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-12">Aucun message pour le moment. Écris le premier message.</div>
                ) : thread.messages.map((msg) => {
                  const mine = msg.sender_user_id === user?.user_id;
                  const isEditing = editingId === msg.message_id;
                  return (
                    <div key={msg.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`group max-w-[82%] rounded-2xl px-4 py-3 shadow-sm ${mine ? "bg-brand-green text-white" : "bg-white text-gray-800"}`}>
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingBody}
                              onChange={(e) => setEditingBody(e.target.value)}
                              className="input min-h-[90px] text-gray-900"
                              maxLength={3000}
                            />
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={cancelEdit} className="rounded-lg bg-gray-100 p-2 text-gray-700 hover:bg-gray-200" title="Annuler">
                                <X className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => saveEdit(msg.message_id)} className="rounded-lg bg-white p-2 text-brand-green hover:bg-emerald-50" title="Enregistrer">
                                <Check className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {msg.image_url && (
                              <a href={fileUrl(msg.image_url)} target="_blank" rel="noopener noreferrer" className="block mb-2">
                                <img
                                  src={fileUrl(msg.image_url)}
                                  alt="Image envoyée"
                                  className="max-h-80 max-w-full rounded-xl object-contain"
                                />
                              </a>
                            )}
                            {msg.body && <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.body}</p>}
                            <div className="mt-1 flex items-center justify-between gap-3">
                              <p className={`text-[11px] ${mine ? "text-white/75" : "text-gray-400"}`}>
                                {formatDate(msg.created_at)} {msg.edited_at ? "· modifié" : ""}
                              </p>
                              <div className={`flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition ${mine ? "text-white/85" : "text-gray-400"}`}>
                                {mine && (
                                  <button type="button" onClick={() => startEdit(msg)} className="rounded p-1 hover:bg-black/10" title="Modifier">
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button type="button" onClick={() => deleteMessage(msg)} className="rounded p-1 hover:bg-black/10" title="Supprimer">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendMessage} className="p-4 border-t border-emerald-100 bg-white space-y-3">
                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={fileUrl(imagePreview)}
                      alt="Image prête à envoyer"
                      className="max-h-40 rounded-xl border border-emerald-100 object-contain"
                    />
                    <button
                      type="button"
                      onClick={removePendingImage}
                      className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white shadow hover:bg-red-600"
                      title="Retirer l’image"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                <div className="flex gap-3">
                  <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 px-3 text-brand-green transition hover:bg-emerald-100" title="Ajouter une photo">
                    <ImageIcon className="h-5 w-5" />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={uploadingImage || sending}
                      className="hidden"
                    />
                  </label>

                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="input min-h-[48px] max-h-32"
                    rows={1}
                    placeholder="Écris ton message privé..."
                    maxLength={3000}
                  />
                  <button type="submit" disabled={sending || uploadingImage || (!body.trim() && !imageUrl)} className="btn-primary self-end">
                    <Send className="h-4 w-4" />
                  </button>
                </div>

                {uploadingImage && <p className="text-xs text-gray-500">Upload de l’image en cours...</p>}
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
